import { useState, useCallback } from "react";
import { useStore } from "../../../store";

import { PaymentIntent, StripeError } from "@stripe/stripe-js";
import { assertError } from "@ecologi/shared";

interface ConfirmPaymentIntentBaseResult {
  success: boolean;
}

interface SuccessResult extends ConfirmPaymentIntentBaseResult {
  success: true;
  paymentIntent: PaymentIntent;
}

interface FailureResult extends ConfirmPaymentIntentBaseResult {
  success: false;
  error: StripeError | Error;
}

type ConfirmPaymentIntentResult = SuccessResult | FailureResult;

export const useConfirmPaymentIntent = () => {
  const { stripe } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<StripeError | Error>(null);

  const confirmPaymentIntent = useCallback(
    async (clientSecret: string): Promise<ConfirmPaymentIntentResult> => {
      if (!stripe) {
        const error = new Error(
          "Payment system is not ready. Please try again."
        );
        setError(error);
        return { success: false, error };
      }

      setIsLoading(true);
      setError(null);
      try {
        const result = await stripe.confirmCardPayment(clientSecret);
        if (result.error) {
          setError(result.error);
          return { success: false, error: result.error };
        }
        if (result.paymentIntent?.status === "succeeded")
          return { success: true, paymentIntent: result.paymentIntent };

        const error = new Error(
          "Payment authentication did not complete successfully."
        );
        setError(error);
        return { success: false, error };
      } catch (err) {
        const normalisedError = assertError(err);
        setError(normalisedError);
        return {
          success: false,
          error: normalisedError,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [stripe]
  );

  return { confirmPaymentIntent, isLoading, error };
};
